/** @typedef {import('headlight-configurable-application').Pict} Pict */

const SelectInputTypeProvider = require('./PictProvider-InputType-Select.js');

const DEFAULT_TEST_RESULT_OPTIONS = [
	{ 'id': 'N/A', 'text': 'N/A' },
	{ 'id': 'PROG', 'text': 'In Progress' },
	{ 'id': 'PEND', 'text': 'Pending' },
	{ 'id': 'FAIL', 'text': 'Failed' },
	{ 'id': 'FAIC', 'text': 'Failed with Comments' },
	{ 'id': 'PASS', 'text': 'Passed' },
	{ 'id': 'PASC', 'text': 'Passed with Comments' },
	{ 'id': 'VOID', 'text': 'Voided' },
];

class SampleTestResultInputType extends SelectInputTypeProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict & { TransactionTracking: any }} */
		this.pict;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#SAMPLE-TEST-RESULT-SELECT-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#SAMPLE-TEST-RESULT-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#SAMPLE-TEST-RESULT-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	getTrackingGUIDID(pInputHTMLID, pRowIndex, pType)
	{
		const tmpType = pType ? `${pType}-` : '';
		if (pRowIndex != null)
		{
			return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}-${pRowIndex}`;
		}
		return `#MATERIALS-ENTITY-TRACKING-GUID-${tmpType}${pInputHTMLID}`;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = {};
		}
		if (!pInput.PictForm.SelectOptions || !Array.isArray(pInput.PictForm.SelectOptions))
		{
			pInput.PictForm.SelectOptions = DEFAULT_TEST_RESULT_OPTIONS;
		}
		//TODO: any custom logic for loading test result codes / etc. here?
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = {};
		}
		if (!pInput.PictForm.SelectOptions || !Array.isArray(pInput.PictForm.SelectOptions))
		{
			pInput.PictForm.SelectOptions = DEFAULT_TEST_RESULT_OPTIONS;
		}
		//TODO: any custom logic for loading test result codes / etc. here?
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput);
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		//TODO: plumb the test result into the configured address
		return tmpResult;
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex);
		//TODO: plumb the test result into the configured address
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the change event for the data in the select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input.
	 * @param {string} pHTMLSelector - The HTML selector of the input.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the super.onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput)}:${pValue}`);
	}

	/**
	 * Handles the change event for tabular data.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the super method.
	 */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput, pRowIndex)}:${pValue}`);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onEvent(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onEventTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onEventTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload, tmpExtraData ] = pEvent.split(':');

		//FIXME: needs revision to work with multi-sample forms
		if (tmpEventType === 'TestStatusAggregate')
		{
			const tmpDestination = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (Array.isArray(tmpDestination))
			{
				let tmpTestResult;
				if (pRowIndex != null)
				{
					tmpTestResult = this.pict.ContentAssignment.readContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex));
				}
				else
				{
					tmpTestResult = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
				}
				tmpDestination.push(tmpTestResult);
			}
		}

		if (tmpEventType === 'SetTestResultByHash')
		{
			const tmpChangedTestHash = tmpTrackingGUID;
			const tmpEvent = `GetTestHash:${this._getTrackingGUID(pInput, pRowIndex)}:_GetTestHashResponse.${pInput.Hash}`;
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent);
			const tmpMarshalDestination = pView.getMarshalDestinationObject();
			const tmpTestHash = tmpMarshalDestination._GetTestHashResponse?.[pInput.Hash];
			if (tmpTestHash)
			{
				delete tmpMarshalDestination._GetTestHashResponse[pInput.Hash];
				if (Object.keys(tmpMarshalDestination._GetTestHashResponse).length === 0)
				{
					delete tmpMarshalDestination._GetTestHashResponse;
				}
			}
			if (tmpTestHash !== tmpChangedTestHash)
			{
				return;
			}
			const tmpSuccessMarkerAddress = tmpExtraData;
			if (pRowIndex != null)
			{
				pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpEventPayload);
				pView.manualMarshalTabularDataToViewByInput(pInput, pRowIndex);
			}
			else
			{
				pView.setDataByInput(pInput, tmpEventPayload);
				pView.manualMarshalDataToViewByInput(pInput);
			}
			if (tmpSuccessMarkerAddress)
			{
				this.pict.manifest.setValueAtAddress(this.pict, tmpSuccessMarkerAddress, true);
			}
			this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`TestResultChanged:${this._getTrackingGUID(pInput)}:${tmpEventPayload}`);
		}

		if (tmpEventType === 'MarkNewTestsInProgress' && pInput.PictForm && pInput.PictForm.MarkTestsInProgressOnSave && pInput.PictForm.MarkTestsInProgressOnSave !== 'false')
		{
			let tmpDataAddress = pInput.DataAddress;
			if (pRowIndex != null)
			{
				tmpDataAddress = pView.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
			}
			let tmpTestResult = pView.sectionManifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDataAddress);
			if (pRowIndex != null)
			{
				tmpTestResult = this.pict.ContentAssignment.readContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex));
			}
			else
			{
				tmpTestResult = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
			}
			if (!tmpTestResult || tmpTestResult === 'N/A')
			{
				tmpTestResult = 'PROG';
				if (pRowIndex != null)
				{
					pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpTestResult);
					pView.manualMarshalTabularDataToViewByInput(pInput, pRowIndex);
				}
				else
				{
					pView.setDataByInput(pInput, tmpTestResult);
					pView.manualMarshalDataToViewByInput(pInput);
				}
			}
		}
	}

	/**
	 * @param {(pError?: Error) => void} fCallback - The callback to call after the data pre-load.
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		const tmpEvent = 'MarkNewTestsInProgress';
		const tmpTransactionGUID = this.pict.getUUID();
		this.pict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			super.onBeforeSaveDataAsync(fCallback);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}


	/**
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 * @param {string} [pType='TEST'] - Default to TEST since that is the only local type we care about here.
	 *
	 * @returns {string|null}
	 */
	_getTrackingGUID(pInput, pRowIndex, pType = 'TEST')
	{
		if (pRowIndex == null)
		{
			switch (pType)
			{
			case 'TEST':
				if (pInput.PictForm?.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
				{
					return pInput.PictForm.TestTrackingGUID;
				}
			default:
				if (pInput.PictForm?.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
				{
					return pInput.PictForm.SampleTrackingGUID;
				}
			}
		}
		const tmpGUID = this.pict.ContentAssignment.readContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType));
		if (typeof tmpGUID === 'string' && tmpGUID.length > 0)
		{
			return tmpGUID;
		}
		return null;
	}

	/**
	 * @param {Object} pInput
	 * @param {number|null} pRowIndex
	 * @param {string} pTrackingGUID
	 * @param {string} [pType='TEST'] - Default to TEST since that is the only local type we care about here.
	 *
	 * @return {void}
	 */
	_setTrackingGUID(pInput, pRowIndex, pTrackingGUID, pType = 'TEST')
	{
		this.pict.ContentAssignment.assignContent(this.getTrackingGUIDID(pInput.Macro.RawHTMLID, pRowIndex, pType), pTrackingGUID);
	}

	/**
	 * Searches for the sample descriptor that this test result should be bound to and inherits its tracking GUID if needed.
	 *
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_bindTrackingGUIDIfNeeded(pView, pGroup, pInput, pRowIndex)
	{
		if (this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		// modalities:
		//
		// single global sample
		// tabular row-based tests
		// tabular row-based sample and tests

		if (pRowIndex != null)
		{
			// tabular
			const tmpLocalManifest = pGroup.supportingManifest;
			if (!tmpLocalManifest || typeof tmpLocalManifest !== 'object' || Array.isArray(tmpLocalManifest))
			{
				this.log.error('SampleTestResultSelector: invalid supporting manifest on group.', { pGroup });
			}
			else
			{
				for (const tmpDescriptor of Object.values(tmpLocalManifest.elementDescriptors))
				{
					if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.TestTrackingGUID)
					{
						const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor, pRowIndex);
						if (tmpTrackingGUID)
						{
							this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
							return;
						}
					}
				}
			}
		}
		// check sibling Descriptors for a sample descriptor
		const tmpGlobalManifest = this.pict.views.PictFormMetacontroller?.manifestDescription;
		if (!tmpGlobalManifest || !tmpGlobalManifest.Descriptors || typeof tmpGlobalManifest.Descriptors !== 'object' || Array.isArray(tmpGlobalManifest.Descriptors))
		{
			this.log.error('SampleTestResultSelector: invalid global manifest on metacontroller.', { pGroup });
			return;
		}
		for (const tmpDescriptor of Object.values(tmpGlobalManifest.Descriptors))
		{
			if (tmpDescriptor.PictForm && tmpDescriptor.PictForm.TestTrackingGUID)
			{
				const tmpTrackingGUID = this._getTrackingGUID(tmpDescriptor);
				if (tmpTrackingGUID)
				{
					this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
					return;
				}
			}
		}
		if (!this._getTrackingGUID(pInput, pRowIndex))
		{
			this.log.warn('SampleTestResultSelector: could not find sibling test descriptor to bind tracking GUID from.');
		}
	}
}

module.exports = SampleTestResultInputType;
module.exports.default_configuration = { AutoSaveDataWithApp: true };
